import { Suspense, useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import ErrorMessage from '@components/ErrorBoundary/ErrorMessage'
import { usePageConfig } from '@hooks/usePageConfig'
import ExternalHeaderContent from '@components/ExternalHeaderContent/ExternalHeaderContent'
import QueryInfoSkeleton from '@components/skeletons/QueryInfoSkeleton/QueryInfoSkeleton'
import QueryResultSkeleton from '@components/skeletons/QueryResultSkeleton/QueryResultSkeleton'
import { useQueryInfo, useRunQuery } from '@hooks/useQuery'
import QueryResult from './QueryResult'
import QueryInfo from './QueryInfo'
import Toast from '@components/Toast/Toast'

import './Query.css'

const QueryBreadcrumb = ({ id }: { id: string }) => {
  const intl = useIntl()
  const item = useQueryInfo(id)
  const crumbs = [
    { label: intl.formatMessage({ id: 'nav.queries' }), path: '/queries' },
    { label: item?.title ?? id, path: `/queries/${id}` }
  ]
  return <ExternalHeaderContent hasBack crumbs={crumbs} />
}

export default function Query() {
  const intl = useIntl()
  const { id } = useParams<{ id: string }>()
  const { runQuery, refreshQueryData, refreshQueryInfo } = useRunQuery()
  const [isLoading, setIsLoading] = useState(false)
  const [toastMessage, setToastMessage] = useState<string | null>(null)

  const handleRun = useCallback(async () => {
    setIsLoading(true)
    try {
      await runQuery(id)
      await Promise.all([refreshQueryData(id), refreshQueryInfo(id)])
      setToastMessage(intl.formatMessage({ id: 'builder.alert.executed' }))
    } catch (e) {
      console.log(e)
    } finally {
      setIsLoading(false)
    }
  }, [runQuery, id, refreshQueryData, refreshQueryInfo, intl])

  usePageConfig('query', <QueryBreadcrumb id={id} />)

  return (
    <>
      <section id="query-content" className="main-content">
        <div className="container">
          <ErrorBoundary errorComponent={ErrorMessage}>
            <Suspense fallback={<QueryInfoSkeleton />}>
              <QueryInfo isLoading={isLoading} id={id} onRun={handleRun} />
            </Suspense>
          </ErrorBoundary>
          <ErrorBoundary errorComponent={ErrorMessage}>
            <Suspense fallback={<QueryResultSkeleton />}>
              <QueryResult isLoading={isLoading} id={id} onRun={handleRun} />
            </Suspense>
          </ErrorBoundary>
        </div>
      </section>
      {toastMessage && (
        <Toast
          message={toastMessage}
          onClose={() => {
            setToastMessage(null)
          }}
        />
      )}
    </>
  )
}
